import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X, Settings2, ShieldCheck } from "lucide-react";
import { Link } from "@tanstack/react-router";

const STORAGE_KEY = "lakrosz_consent";
const VERSION = 1;

export type ConsentState = {
  version: number;
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  decidedAt: string;
};

const DEFAULT: ConsentState = {
  version: VERSION,
  necessary: true,
  analytics: false,
  marketing: false,
  decidedAt: "",
};

function readConsent(): ConsentState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentState;
    if (parsed.version !== VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeConsent(c: ConsentState) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(c));
  window.dispatchEvent(new CustomEvent("lakrosz:consent", { detail: c }));
}

/** Megnyitja a Süti beállítások modált bárhonnan. */
export function openCookiePreferences() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event("lakrosz:open-cookie-preferences"));
}

/** Lekérdezi az aktuális hozzájárulást (SSR-safe). */
export function getConsent(): ConsentState {
  return readConsent() ?? DEFAULT;
}

export function CookieConsent() {
  const [decided, setDecided] = useState<boolean>(true); // SSR-safe: ne villanjon
  const [showPrefs, setShowPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  // initial mount
  useEffect(() => {
    const existing = readConsent();
    if (!existing) {
      setDecided(false);
    } else {
      setAnalytics(existing.analytics);
      setMarketing(existing.marketing);
    }
    const openHandler = () => {
      const cur = readConsent();
      if (cur) {
        setAnalytics(cur.analytics);
        setMarketing(cur.marketing);
      }
      setShowPrefs(true);
    };
    window.addEventListener("lakrosz:open-cookie-preferences", openHandler);
    return () =>
      window.removeEventListener("lakrosz:open-cookie-preferences", openHandler);
  }, []);

  const save = useCallback((a: boolean, m: boolean) => {
    const next: ConsentState = {
      version: VERSION,
      necessary: true,
      analytics: a,
      marketing: m,
      decidedAt: new Date().toISOString(),
    };
    writeConsent(next);
    setDecided(true);
    setShowPrefs(false);
  }, []);

  const acceptAll = () => save(true, true);
  const acceptNecessary = () => save(false, false);
  const saveCustom = () => save(analytics, marketing);

  return (
    <>
      {/* BANNER */}
      <AnimatePresence>
        {!decided && !showPrefs && (
          <motion.div
            initial={{ y: 120, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 120, opacity: 0 }}
            transition={{ type: "spring", stiffness: 220, damping: 28 }}
            className="fixed inset-x-3 bottom-3 md:inset-x-6 md:bottom-6 z-[100]"
            role="dialog"
            aria-live="polite"
            aria-label="Süti tájékoztató"
          >
            <div className="max-w-5xl mx-auto rounded-2xl border border-primary/40 bg-background/95 backdrop-blur-xl shadow-2xl p-5 md:p-6">
              <div className="flex flex-col md:flex-row md:items-center gap-5">
                <div className="flex items-start gap-3 flex-1">
                  <div className="shrink-0 w-11 h-11 rounded-xl bg-primary/15 grid place-items-center">
                    <Cookie className="w-5 h-5 text-primary" />
                  </div>
                  <div className="text-sm leading-relaxed">
                    <div className="font-display text-lg tracking-wide mb-1">
                      Sütiket használunk
                    </div>
                    Az oldal működéséhez szükséges sütiket alapértelmezetten használjuk.
                    Statisztikai és marketing sütiket csak a hozzájárulásoddal töltünk be.
                    Részletek a{" "}
                    <Link to="/cookie-tajekoztato" className="text-primary underline">
                      Cookie tájékoztatóban
                    </Link>
                    .
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 md:shrink-0">
                  <button
                    type="button"
                    onClick={() => setShowPrefs(true)}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-border hover:border-primary text-sm font-medium transition-colors"
                  >
                    <Settings2 className="w-4 h-4" />
                    Beállítások
                  </button>
                  <button
                    type="button"
                    onClick={acceptNecessary}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-border hover:border-primary text-sm font-medium transition-colors"
                  >
                    Csak szükséges
                  </button>
                  <button
                    type="button"
                    onClick={acceptAll}
                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-primary to-primary-glow text-primary-foreground text-sm font-semibold"
                  >
                    Mindet elfogadom
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* PREFERENCES MODAL */}
      <AnimatePresence>
        {showPrefs && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm grid place-items-center p-4"
            onClick={() => decided && setShowPrefs(false)}
          >
            <motion.div
              initial={{ scale: 0.95, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.95, y: 20, opacity: 0 }}
              transition={{ type: "spring", stiffness: 260, damping: 28 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg rounded-3xl bg-surface border border-border shadow-2xl overflow-hidden"
            >
              <div className="p-6 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/15 grid place-items-center">
                    <Settings2 className="w-5 h-5 text-primary" />
                  </div>
                  <h2 className="font-display text-2xl tracking-wide">Süti beállítások</h2>
                </div>
                {decided && (
                  <button
                    type="button"
                    onClick={() => setShowPrefs(false)}
                    aria-label="Bezárás"
                    className="w-9 h-9 rounded-full grid place-items-center hover:bg-surface-elevated"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
                <CategoryRow
                  title="Szükséges sütik"
                  description="Az oldal alapvető működéséhez (pl. hozzájárulás mentése). Mindig aktív."
                  checked
                  disabled
                  onChange={() => {}}
                />
                <CategoryRow
                  title="Analitikai sütik"
                  description="Anonim statisztikák (pl. Google Analytics) az oldal fejlesztéséhez."
                  checked={analytics}
                  onChange={setAnalytics}
                />
                <CategoryRow
                  title="Marketing sütik"
                  description="Hirdetési célú mérés (pl. Meta Pixel). Csak a hozzájárulásoddal."
                  checked={marketing}
                  onChange={setMarketing}
                />
                <p className="text-xs text-muted-foreground pt-2 flex items-center gap-2">
                  <ShieldCheck className="w-3.5 h-3.5 text-primary" />
                  Részletek:{" "}
                  <Link to="/cookie-tajekoztato" className="underline hover:text-primary">
                    Cookie tájékoztató
                  </Link>{" "}
                  ·{" "}
                  <Link
                    to="/adatkezelesi-tajekoztato"
                    className="underline hover:text-primary"
                  >
                    Adatkezelési tájékoztató
                  </Link>
                </p>
              </div>

              <div className="p-5 border-t border-border flex flex-col-reverse sm:flex-row gap-2 sm:justify-end bg-background/40">
                <button
                  type="button"
                  onClick={acceptNecessary}
                  className="px-4 py-2.5 rounded-full border border-border hover:border-primary text-sm font-medium"
                >
                  Csak szükséges
                </button>
                <button
                  type="button"
                  onClick={saveCustom}
                  className="px-4 py-2.5 rounded-full border border-primary text-primary hover:bg-primary/10 text-sm font-medium"
                >
                  Beállítások mentése
                </button>
                <button
                  type="button"
                  onClick={acceptAll}
                  className="px-5 py-2.5 rounded-full bg-gradient-to-r from-primary to-primary-glow text-primary-foreground text-sm font-semibold"
                >
                  Mindet elfogadom
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function CategoryRow({
  title,
  description,
  checked,
  disabled,
  onChange,
}: {
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div
      className={`flex items-start justify-between gap-4 p-4 rounded-2xl border ${
        disabled ? "border-border bg-background/40" : "border-border bg-background/40"
      }`}
    >
      <div className="flex-1">
        <div className="font-semibold text-sm">{title}</div>
        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={title}
        disabled={disabled}
        onClick={() => !disabled && onChange(!checked)}
        className={`relative w-11 h-6 rounded-full transition-colors shrink-0 mt-1 ${
          checked ? "bg-primary" : "bg-border"
        } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-background shadow transition-transform ${
            checked ? "translate-x-5" : ""
          }`}
        />
      </button>
    </div>
  );
}

/**
 * Analitikai / marketing skriptek feltételes betöltése.
 * Csak a hozzájárulás megadása után tölt be külső scripteket.
 *
 * Pótold a TODO-knál a saját mérési azonosítóid:
 *  - Meta Pixel: 123456789
 */
export function ConsentScripts() {
  useEffect(() => {
    const GA_ID =
      (import.meta.env.VITE_LOVABLE_CONNECTOR_GOOGLE_ANALYTICS_API_KEY as string | undefined) ||
      "G-ZG0Y6WNT5D";

    // A Google-címke mindig betöltődik (hogy a Google észlelni tudja),
    // de Consent Mode v2 alapból megtagadja a mérést hozzájárulás nélkül.
    if (GA_ID && !document.getElementById("ga-script")) {
      const inlineInit = document.createElement("script");
      inlineInit.id = "ga-inline";
      inlineInit.text = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('consent','default',{ad_storage:'denied',ad_user_data:'denied',ad_personalization:'denied',analytics_storage:'denied'});gtag('js',new Date());gtag('config','${GA_ID}');`;
      document.head.appendChild(inlineInit);
      const s = document.createElement("script");
      s.id = "ga-script";
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
      document.head.appendChild(s);
    }

    const apply = (c: ConsentState) => {
      // GOOGLE ANALYTICS 4 — hozzájárulás frissítése
      (window as unknown as { gtag?: (...a: unknown[]) => void }).gtag?.(
        "consent",
        "update",
        {
          analytics_storage: c.analytics ? "granted" : "denied",
          ad_storage: c.marketing ? "granted" : "denied",
          ad_user_data: c.marketing ? "granted" : "denied",
          ad_personalization: c.marketing ? "granted" : "denied",
        },
      );


      // MICROSOFT CLARITY — project ID: ymcrmauk7u
      const CLARITY_ID = "ymcrmauk7u";
      if (c.analytics && CLARITY_ID && !document.getElementById("clarity-script")) {
        const s = document.createElement("script");
        s.id = "clarity-script";
        s.type = "text/javascript";
        s.text = `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window,document,"clarity","script","${CLARITY_ID}");`;
        document.head.appendChild(s);
      }

      // META PIXEL — TODO: állítsd be a saját Pixel ID-d
      const META_ID: string | null = null; // pl. "1234567890"
      if (c.marketing && META_ID && !document.getElementById("meta-pixel")) {
        const s = document.createElement("script");
        s.id = "meta-pixel";
        s.text = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${META_ID}');fbq('track','PageView');`;
        document.head.appendChild(s);
      }
    };
    apply(getConsent());
    const handler = (e: Event) => apply((e as CustomEvent<ConsentState>).detail);
    window.addEventListener("lakrosz:consent", handler);
    return () => window.removeEventListener("lakrosz:consent", handler);
  }, []);
  return null;
}
